import type {
  CmsPagesExportFile,
  CmsPagesExportImage,
  CmsPagesImportFile,
  CmsPagesImportPreview,
} from "../../../types/cms";

type ImageKey = { hash: string; ext: string };

export function imageName(image: ImageKey): string {
  return `${image.hash}.${image.ext}`;
}

export function withoutImageData(file: CmsPagesExportFile): CmsPagesImportFile {
  if (file.version === 1) {
    return file;
  }
  return {
    ...file,
    images: file.images.map(({ hash, ext, contentType, size }) => ({
      hash,
      ext,
      contentType,
      size,
    })),
  };
}

export type ImportImageCounts = { present: number; missing: number };

export function importImageCounts(preview: CmsPagesImportPreview): ImportImageCounts {
  const present = preview.images.filter(image => image.status === "present").length;
  return { present, missing: preview.images.length - present };
}

export function importImagesSummary(
  preview: CmsPagesImportPreview,
  file: CmsPagesExportFile,
): string | null {
  if (preview.images.length === 0) {
    return null;
  }
  const { present, missing } = importImageCounts(preview);
  if (file.version === 1) {
    const summary = `Images: ${present} already on this site, ${missing} missing.`;
    return missing === 0
      ? summary
      : `${summary} This file comes from an earlier version of the CMS and carries no images, so the import will fail until they're on this site. Export the pages again to bring their images.`;
  }
  return `Images: ${present} already on this site, ${missing} to upload before importing.`;
}

export function imagesToUpload(
  preview: CmsPagesImportPreview,
  file: CmsPagesExportFile,
  uploaded: ReadonlySet<string>,
): CmsPagesExportImage[] {
  if (file.version === 1) {
    return [];
  }
  const missing = new Set(
    preview.images.filter(image => image.status === "missing").map(imageName),
  );
  return file.images.filter(
    image => missing.has(imageName(image)) && !uploaded.has(imageName(image)),
  );
}

export function toImageFile(image: CmsPagesExportImage): File {
  const bytes = Uint8Array.from(atob(image.data), char => char.charCodeAt(0));
  return new File([bytes], imageName(image), { type: image.contentType });
}

export function storedImageName(stored: { hash: string; url: string }): string {
  const path = stored.url.split(/[?#]/)[0] ?? "";
  const ext = /\.([a-z0-9]+)$/i.exec(path)?.[1] ?? "";
  return imageName({ hash: stored.hash, ext });
}
